/**
 * Simple util to pluralize a given string.
 *
 * @param text Text to pluralize
 * @returns Pluralized text
 */
export const pluralizeString = (text: string): string => {
  if (text.substring(-1) === 's') {
    return `${text}'`;
  }
  return `${text}'s`;
};

/**
 * Checks if a passed string is a base64 encoded image (png, jpg, ect.)
 *
 * @param data base64 encoded string
 * @returns boolean
 */
export const isBase64Image = (data: string): boolean => {
  const base64ImageRegex =
    /^data:image\/(png|jpg|jpeg|gif|bmp|webp);base64,(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;
  return base64ImageRegex.test(data);
};

/**
 * Checks if a url string starts with an `http[s]://` protocol, and adds `https://` if it does not. If the url
 * begins with `localhost` or `host.docker.internal`, the `http` protocol is used.
 *
 * @param {string} url
 * @param {('http://' | 'https://')} [protocol='https://'] The protocol to add, if necessary. Defaults to `https://`.
 * @return {*}  {string} the url which is guaranteed to have an `http(s)://` protocol.
 */
export const ensureProtocol = (
  url: string,
  protocol: 'http://' | 'https://' = 'https://'
): string => {
  if (url.startsWith('localhost') || url.startsWith('host.docker.internal')) {
    return `${'http://'}${url}`;
  }

  if (
    url.startsWith('https://') ||
    url.startsWith('http://localhost') ||
    url.startsWith('http://host.docker.internal')
  ) {
    return url;
  }

  if (url.startsWith('http://')) {
    // If protocol is HTTPS, upgrade the URL
    if (protocol === 'https://') {
      return `${'https://'}${url.slice(7)}`;
    }
  }

  return `${protocol}${url}`;
};

/**
 * convert a number date YYYYMMDD into a date string.
 *
 * @param value date number to format
 * @returns formatted date string
 */
export const getDateString = (value: number): string => {
  const year = Math.floor(value / 10000);
  const month = Math.floor((value % 10000) / 100) - 1;
  const day = value % 100;
  const dateObj = new Date(year, month, day);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(dateObj);
};

/**
 * Builds a URL from multiple (possibly null or undefined) url parts, stripping any
 * double slashes from the resulting URL.
 *
 * @param {(string | undefined)[]} urlParts The parts of the URL
 * @returns The built URL
 */
export const buildUrl = (...urlParts: (string | undefined)[]): string => {
  return urlParts
    .filter((urlPart): urlPart is string => Boolean(urlPart))
    .map((urlPart) => String(urlPart).trim()) // Trim leading and trailing whitespace
    .filter(Boolean)
    .join('/')
    .replace(/([^:]\/)\/+/g, '$1'); // Trim double slashes
};

/**
 * Mobile and responsive design utilities
 */
import { Dimensions } from 'react-native';

export interface DeviceInfo {
  width: number;
  height: number;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  orientation: 'portrait' | 'landscape';
}

/**
 * Get current device information including screen dimensions and device type
 * @returns DeviceInfo object containing device characteristics
 */
export const getDeviceInfo = (): DeviceInfo => {
  const { width, height } = Dimensions.get('window');
  const isMobile = width < 768;
  const isTablet = width >= 768 && width < 1024;
  const isDesktop = width >= 1024;
  const orientation = width > height ? 'landscape' : 'portrait';

  return {
    width,
    height,
    isMobile,
    isTablet,
    isDesktop,
    orientation
  };
};

/**
 * Get responsive padding based on device size
 * @param mobile Mobile padding value
 * @param tablet Tablet padding value (optional, defaults to mobile * 1.5)
 * @param desktop Desktop padding value (optional, defaults to mobile * 2)
 * @returns Appropriate padding for current device
 */
export const getResponsivePadding = (mobile: number, tablet?: number, desktop?: number): number => {
  const { isMobile, isTablet } = getDeviceInfo();

  if (isMobile) return mobile;
  if (isTablet) return tablet || mobile * 1.5;
  return desktop || mobile * 2;
};

/**
 * Get responsive font size based on device size
 * @param mobile Mobile font size
 * @param tablet Tablet font size (optional, defaults to mobile * 1.1)
 * @param desktop Desktop font size (optional, defaults to mobile * 1.2)
 * @returns Appropriate font size for current device
 */
export const getResponsiveFontSize = (
  mobile: number,
  tablet?: number,
  desktop?: number
): number => {
  const { isMobile, isTablet } = getDeviceInfo();

  if (isMobile) return mobile;
  if (isTablet) return tablet || mobile * 1.1;
  return desktop || mobile * 1.2;
};

/**
 * Get responsive margin based on device size
 * @param mobile Mobile margin value
 * @param tablet Tablet margin value (optional, defaults to mobile * 1.5)
 * @param desktop Desktop margin value (optional, defaults to mobile * 2)
 * @returns Appropriate margin for current device
 */
export const getResponsiveMargin = (mobile: number, tablet?: number, desktop?: number): number => {
  const { isMobile, isTablet } = getDeviceInfo();

  if (isMobile) return mobile;
  if (isTablet) return tablet || mobile * 1.5;
  return desktop || mobile * 2;
};

/**
 * Get responsive container max width
 * @returns Appropriate max width for current device
 */
export const getResponsiveContainerWidth = (): number => {
  const { width, isMobile, isTablet } = getDeviceInfo();

  if (isMobile) return width * 0.95; // 95% of screen width on mobile
  if (isTablet) return Math.min(width * 0.85, 600); // 85% of screen width or 600px max on tablet
  return Math.min(width * 0.7, 800); // 70% of screen width or 800px max on desktop
};

/**
 * Fluid responsive font size that scales smoothly between breakpoints
 * @param mobileSize Font size for mobile devices (< 768px)
 * @param tabletSize Font size for tablet devices (768-1024px)
 * @param desktopSize Font size for desktop devices (> 1024px)
 * @param minSize Minimum font size (optional)
 * @param maxSize Maximum font size (optional)
 * @returns Smoothly interpolated font size
 */
export const getFluidFontSize = (
  mobileSize: number,
  tabletSize?: number,
  desktopSize?: number,
  minSize?: number,
  maxSize?: number
): number => {
  const { width } = getDeviceInfo();
  const tablet = tabletSize || mobileSize * 1.1;
  const desktop = desktopSize || mobileSize * 1.2;

  let fontSize: number;

  if (width < 768) {
    // Mobile to tablet transition (smooth interpolation)
    const progress = Math.min(width / 768, 1);
    fontSize = mobileSize + (tablet - mobileSize) * progress;
  } else if (width < 1024) {
    // Tablet to desktop transition
    const progress = (width - 768) / (1024 - 768);
    fontSize = tablet + (desktop - tablet) * progress;
  } else {
    // Desktop with optional scaling beyond 1024px
    const baseProgress = Math.min((width - 1024) / 1024, 1);
    fontSize = desktop + desktop * 0.1 * baseProgress;
  }

  // Apply min/max constraints if provided
  if (minSize) fontSize = Math.max(fontSize, minSize);
  if (maxSize) fontSize = Math.min(fontSize, maxSize);

  return Math.round(fontSize * 100) / 100; // Round to 2 decimal places
};

/**
 * Fluid responsive spacing that scales smoothly between breakpoints
 * @param mobileSpacing Spacing for mobile devices
 * @param tabletSpacing Spacing for tablet devices (optional)
 * @param desktopSpacing Spacing for desktop devices (optional)
 * @returns Smoothly interpolated spacing value
 */
export const getFluidSpacing = (
  mobileSpacing: number,
  tabletSpacing?: number,
  desktopSpacing?: number
): number => {
  const { width } = getDeviceInfo();
  const tablet = tabletSpacing || mobileSpacing * 1.5;
  const desktop = desktopSpacing || mobileSpacing * 2;

  if (width < 768) {
    const progress = Math.min(width / 768, 1);
    return mobileSpacing + (tablet - mobileSpacing) * progress;
  } else if (width < 1024) {
    const progress = (width - 768) / (1024 - 768);
    return tablet + (desktop - tablet) * progress;
  } else {
    const baseProgress = Math.min((width - 1024) / 1024, 1);
    return desktop + desktop * 0.2 * baseProgress;
  }
};

/**
 * Viewport-based scaling function similar to CSS clamp()
 * @param minValue Minimum value at smallest screen
 * @param maxValue Maximum value at largest screen
 * @param minViewport Minimum viewport width (default: 320px)
 * @param maxViewport Maximum viewport width (default: 1200px)
 * @returns Clamped and scaled value
 */
export const getClampedValue = (
  minValue: number,
  maxValue: number,
  minViewport: number = 320,
  maxViewport: number = 1200
): number => {
  const { width } = getDeviceInfo();

  if (width <= minViewport) return minValue;
  if (width >= maxViewport) return maxValue;

  // Linear interpolation between min and max
  const progress = (width - minViewport) / (maxViewport - minViewport);
  const scaledValue = minValue + (maxValue - minValue) * progress;

  return Math.max(minValue, Math.min(maxValue, scaledValue));
};

/**
 * Container query-like responsive values based on container width
 * @param containerWidth The width of the container element
 * @param breakpoints Object with breakpoint values
 * @returns Appropriate value for container width
 */
export const getContainerResponsiveValue = <T>(
  containerWidth: number,
  breakpoints: {
    sm?: T;
    md?: T;
    lg?: T;
    xl?: T;
    default: T;
  }
): T => {
  if (containerWidth < 400 && breakpoints.sm) return breakpoints.sm;
  if (containerWidth < 768 && breakpoints.md) return breakpoints.md;
  if (containerWidth < 1024 && breakpoints.lg) return breakpoints.lg;
  if (containerWidth >= 1024 && breakpoints.xl) return breakpoints.xl;
  return breakpoints.default;
};

/**
 * Enhanced responsive font size with better scaling options
 * @param baseSize Base font size for mobile
 * @param options Configuration options for scaling
 * @returns Optimized font size for current device
 */
export const getEnhancedResponsiveFontSize = (
  baseSize: number,
  options: {
    scalingFactor?: number; // How much to scale up for larger screens
    minSize?: number; // Minimum font size
    maxSize?: number; // Maximum font size
    useFluidScaling?: boolean; // Use smooth interpolation
  } = {}
): number => {
  const {
    scalingFactor = 0.2,
    minSize = baseSize * 0.8,
    maxSize = baseSize * 1.6,
    useFluidScaling = true
  } = options;

  if (useFluidScaling) {
    return getFluidFontSize(
      baseSize,
      baseSize * (1 + scalingFactor * 0.5),
      baseSize * (1 + scalingFactor),
      minSize,
      maxSize
    );
  }

  return getResponsiveFontSize(
    baseSize,
    baseSize * (1 + scalingFactor * 0.5),
    baseSize * (1 + scalingFactor)
  );
};

/**
 * Organized font scale system for consistent typography
 * Creates a clear hierarchy with semantic naming
 */
export const FontScale = {
  // Card and credential text hierarchy
  cardTitle: () => getFluidFontSize(16, 18, 20, 14, 22), // Main card titles (name)
  cardSubtitle: () => getFluidFontSize(14, 15, 16, 12, 18), // Card subtitles (email, issuer)
  cardHeader: () => getFluidFontSize(14, 15, 16, 12, 18), // Section headers
  cardBody: () => getFluidFontSize(13, 14, 15, 11, 17), // Body text content
  cardCaption: () => getFluidFontSize(12, 13, 14, 10, 16), // Small labels and captions

  // Interactive elements
  button: () => getFluidFontSize(14, 15, 16, 12, 18), // Button text
  link: () => getFluidFontSize(12, 13, 14, 10, 16), // Link text

  // Field and form elements
  fieldLabel: () => getFluidFontSize(13, 14, 15, 11, 17), // Field labels
  fieldValue: () => getFluidFontSize(15, 16, 17, 13, 19), // Field values
  fieldHidden: () => getFluidFontSize(18, 19, 20, 16, 22), // Hidden field indicators

  // Page level typography
  pageTitle: () => getFluidFontSize(20, 22, 24, 18, 28), // Page titles
  sectionTitle: () => getFluidFontSize(16, 18, 20, 14, 22), // Section titles
  bodyText: () => getFluidFontSize(14, 15, 16, 12, 18) // Regular body text
} as const;
